import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Theater, Speaker, Tv, Wrench, PenTool, ArrowRight, Clock, CheckCircle2, Award, AlertCircle, WifiOff, RefreshCw } from 'lucide-react';
import SEO from '../components/SEO';
import { fetchFeaturedSystems, getSystemImageUrl, type System } from '../lib/gallery';
import OptimizedImage from '../components/OptimizedImage';

const Home = () => {
  const [featuredSystems, setFeaturedSystems] = useState<System[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ type: 'connection' | 'data'; message: string } | null>(null);
  const [retrying, setRetrying] = useState(false);
  
  const loadFeaturedSystems = async () => {
    try {
      setLoading(true);
      setError(null);
      setRetrying(false);

      const systems = await fetchFeaturedSystems();
      setFeaturedSystems(systems);
    } catch (error) {
      // Check if user is offline
      if (!navigator.onLine) {
        setError({
          type: 'connection',
          message: 'You appear to be offline. Please check your internet connection and try again.'
        });
      } else if (error instanceof Error && error.message.includes('Failed to fetch')) {
        setError({
          type: 'connection',
          message: 'Unable to connect to the server. Please check your connection and try again.'
        });
      } else {
        setError({
          type: 'data',
          message: 'Failed to load featured systems. Please try again.'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFeaturedSystems();
  }, []);

  const handleRetry = async () => {
    setRetrying(true);
    await loadFeaturedSystems();
  };

  const services = [
    {
      icon: <Theater className="w-12 h-12 text-secondary" />,
      title: "Custom Theater Design",
      description: "Transform your space into a cinematic masterpiece with our expert theater room design services."
    },
    {
      icon: <Speaker className="w-12 h-12 text-secondary" />,
      title: "Premium Audio Systems",
      description: "Experience crystal-clear sound with our professional-grade audio installations."
    },
    {
      icon: <Tv className="w-12 h-12 text-secondary" />,
      title: "Video Excellence",
      description: "Stunning 4K/8K displays and projectors for the ultimate viewing experience."
    },
    {
      icon: <Wrench className="w-12 h-12 text-secondary" />,
      title: "Expert Installation",
      description: "Professional installation ensuring perfect performance and reliability."
    }
  ];

  const stats = [
    {
      value: "30+",
      label: "Years Experience",
      icon: <Clock className="w-8 h-8 text-secondary" />
    },
    {
      value: "1000+",
      label: "Projects Completed",
      icon: <CheckCircle2 className="w-8 h-8 text-secondary" />
    },
    {
      value: "100%",
      label: "Satisfaction Rate",
      icon: <Award className="w-8 h-8 text-secondary" />
    }
  ];

  return (
    <>
      <SEO
        title="Custom Home Theater Design & Installation in Middle Tennessee"
        description="Transform your space with expert home theater design and installation services. Premium audio-visual solutions tailored to your needs in Nashville, Brentwood, Franklin, and surrounding areas."
        keywords={[
          'custom home theater',
          'home theater installation',
          'audio video installation',
          'Nashville home theater',
          'Middle Tennessee AV',
          'home entertainment systems',
          'professional installation',
          'theater room design',
          'surround sound installation',
          'home automation'
        ]}
        path="/"
      />

      {/* Hero Section */}
      <section className="relative bg-primary min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/90 via-primary/85 to-primary/80" />
          <OptimizedImage
            src="https://i.imgur.com/jDmDRrK.jpeg"
            alt="Luxury Home Theater"
            className="w-full h-full object-cover opacity-30"
            priority={true}
            sizes="100vw"
            showSkeleton={false}
          />
        </div>
        
        <div className="container relative z-10 mx-auto px-4 py-20">
          <div className="max-w-5xl mx-auto text-center flex flex-col min-h-[calc(100vh-160px)] justify-center">
            <div className="space-y-8">
              <h1 className="text-6xl md:text-7xl lg:text-8xl font-heading font-bold text-white mb-8 tracking-tight leading-tight">
                Experience Cinema<br />at Home
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
                Custom home theater design and installation for the ultimate entertainment experience in Middle Tennessee
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center mb-20">
                <Link
                  to="/gallery"
                  className="inline-flex items-center justify-center bg-secondary hover:bg-secondary-light text-primary px-8 py-4 rounded-lg transition-colors font-semibold group text-lg"
                >
                  View Our Projects
                  <ArrowRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg transition-colors font-semibold text-lg"
                >
                  Get Started
                </Link>
              </div>

              
              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-4xl mx-auto">
                {stats.map((stat, index) => (
                  <div
                    key={stat.label}
                    className="flex flex-col items-center"
                  >
                    {stat.icon}
                    <div className="mt-4">
                      <div className="text-4xl font-bold text-secondary mb-2">{stat.value}</div>
                      <div className="text-gray-400 uppercase tracking-wider text-sm">{stat.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-primary mb-6">
              Premium Home Theater Services
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              From design to installation, we deliver exceptional audio-visual experiences
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <div
                key={service.title}
                className="bg-accent p-8 rounded-2xl text-center hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="mb-6 transform hover:scale-110 transition-transform duration-300 flex justify-center">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-heading font-semibold text-primary mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-24 bg-accent">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-primary mb-6">
              Featured Installations
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Explore our latest custom home theater and audio installations
            </p>
          </div>

          {error ? (
            <div className="max-w-2xl mx-auto p-6 bg-red-50 rounded-lg flex items-start gap-4">
              <div className="flex-shrink-0 mt-1">
                {error.type === 'connection' ? (
                  <WifiOff className="w-5 h-5 text-red-600" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600" />
                )}
              </div>
              <div className="flex-grow">
                <p className="text-red-700 font-medium">{error.message}</p>
                <button
                  onClick={handleRetry}
                  disabled={retrying}
                  className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-red-700 hover:text-red-800 disabled:opacity-50"
                >
                  {retrying ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Try Again
                </button>
              </div>
            </div>
          ) : loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[1, 2, 3].map((n) => (
                <div key={n} className="animate-pulse">
                  <div className="bg-gray-200 h-64 rounded-2xl mb-4" />
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : featuredSystems.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuredSystems.map((system, index) => (
                <div
                  key={system.id}
                  className="bg-white rounded-2xl shadow-xl overflow-hidden transform hover:scale-105 transition-all duration-300"
                >
                  <div className="relative">
                    <OptimizedImage
                      src={getSystemImageUrl(system)}
                      alt={system.title}
                      className="h-64"
                      aspectRatio="landscape"
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  </div>
                  <div className="p-8">
                    <h3 className="text-2xl font-heading font-semibold mb-4 text-primary">
                      {system.title}
                    </h3>
                    <p className="text-gray-600 mb-6 line-clamp-2">
                      {system.description}
                    </p>
                    <Link
                      to={`/gallery/${system.id}`}
                      className="text-secondary hover:text-secondary-light font-medium inline-flex items-center group"
                    >
                      Learn More
                      <ArrowRight className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-600">
              No featured installations available.
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-primary text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
              Why Choose Pinnacle
            </h2>
            <p className="text-xl text-gray-300">
              We bring together cutting-edge technology and expert craftsmanship
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <PenTool className="w-16 h-16 text-secondary" />,
                title: "Expert Design",
                description: "Custom solutions tailored to your space and preferences"
              },
              {
                icon: <Speaker className="w-16 h-16 text-secondary" />,
                title: "Premium Equipment",
                description: "Top-tier audio and visual components from leading brands"
              },
              {
                icon: <Wrench className="w-16 h-16 text-secondary" />,
                title: "Professional Installation",
                description: "Meticulous attention to detail in every installation"
              }
            ].map((feature, index) => (
              <div
                key={index}
                className="bg-white/5 backdrop-blur-sm p-8 rounded-2xl text-center transform hover:scale-105 transition-all duration-300"
              >
                <div className="flex justify-center mb-6">{feature.icon}</div>
                <h3 className="text-2xl font-heading font-semibold mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-300">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center bg-white rounded-3xl p-12 shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-secondary/10 to-primary/5"></div>
            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-primary mb-6">
                Ready to Transform Your Space?
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Contact us today to begin designing your dream home theater
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center bg-primary hover:bg-primary-light text-white px-8 py-4 rounded-lg transition-colors font-semibold text-lg group"
              >
                Get Started
                <ArrowRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;